// Copyright (c) Ping. All rights reserved.
//
// Single decoder for incoming Application envelopes. The current wire
// format is a JSON wrapper
//   { kind: "com.ping.X", payload: {...} }
// per `docs/architecture/types-and-schemas.md` §1.1's "wrapper today,
// CBOR tomorrow" migration plan. `parseEnvelopeJson` reads either:
//   - a `Uint8Array` plaintext straight off the MLS pipeline,
//   - a `string` (the `Message.body` column persists envelopes as
//     stringified JSON for non-text kinds — see `sendVoiceMessage`).
// and returns a typed `ParsedAppEvent` with the decoded body, or
// `undefined` when the wrapper isn't recognized.
//
// The decoder is tolerant by design:
//   - unknown kinds map to `{ kind: <raw>, type: undefined,
//     payload: <raw> }` so callers can surface "Unrenderable message"
//     instead of dropping the row (§1.3 forward-compat).
//   - plain UTF-8 text that fails JSON.parse is treated as a legacy
//     `com.ping.text` body with the raw string in `payload.body`.
//     This covers messages from old clients that pre-date the JSON
//     wrapper rollout.

import {
  appEventTypeForKind,
  isAppEventKind,
  isSidecarKind,
  type AppEventKind,
  type SidecarKind,
} from './kinds';
import type {
  AppEventType,
  BlobRef,
  CallAcceptBody,
  CallDeclineBody,
  CallEndBody,
  CallHandRaiseBody,
  CallHandoffBody,
  CallInviteBody,
  CallReactionBody,
  CallRingingBody,
  CallRosterBody,
  EditBody,
  FileBody,
  ForwardBody,
  GameAbortBody,
  GameCreateBody,
  GameJoinBody,
  GameKind,
  GameMoveBody,
  GameOutcomeBody,
  GamePayout,
  GameRevealBody,
  GameStakes,
  GroupAvatarBody,
  GroupNameBody,
  GroupTopicBody,
  ImageBody,
  InviteBody,
  KickReasonBody,
  LinkPreviewBody,
  LocationOnceBody,
  MeetingCancelBody,
  MeetingCreateBody,
  MeetingRsvpBody,
  MeetingUpdateBody,
  Mention,
  NoteDeleteBody,
  NoteUpsertBody,
  PinBody,
  PollCreateBody,
  PollVoteBody,
  ReactionBody,
  ReactionRemoveBody,
  RedactBody,
  SaveBody,
  UnsaveBody,
  BlockedUserUpsertBody,
  BlockedUserRemoveBody,
  DisappearingPolicyBody,
  MemberRoleSetBody,
  GroupMemberRole,
  ConvStateBody,
  ReadPointerBody,
  ReminderSetBody,
  ReminderClearBody,
  SelfProfileBody,
  StickerBody,
  TaskDeleteBody,
  TaskUpsertBody,
  TourSeenBody,
  TextBody,
  ThreadRootBody,
  UnpinBody,
  VideoBody,
  VoiceBody,
} from './types';

export type AppEventBody =
  | TextBody
  | ImageBody
  | VideoBody
  | VoiceBody
  | FileBody
  | LocationOnceBody
  | StickerBody
  | LinkPreviewBody
  | EditBody
  | RedactBody
  | ForwardBody
  | ReactionBody
  | ReactionRemoveBody
  | ThreadRootBody
  | GroupNameBody
  | GroupAvatarBody
  | GroupTopicBody
  | InviteBody
  | KickReasonBody
  | PinBody
  | UnpinBody
  | SaveBody
  | UnsaveBody
  | BlockedUserUpsertBody
  | BlockedUserRemoveBody
  | DisappearingPolicyBody
  | MemberRoleSetBody
  | PollCreateBody
  | PollVoteBody
  | NoteUpsertBody
  | NoteDeleteBody
  | TaskUpsertBody
  | TaskDeleteBody
  | SelfProfileBody
  | ConvStateBody
  | ReadPointerBody
  | ReminderSetBody
  | ReminderClearBody
  | TourSeenBody
  | GameCreateBody
  | GameJoinBody
  | GameMoveBody
  | GameRevealBody
  | GameOutcomeBody
  | GameAbortBody
  | CallInviteBody
  | CallAcceptBody
  | CallDeclineBody
  | CallEndBody
  | CallRingingBody
  | CallHandRaiseBody
  | CallReactionBody
  | CallRosterBody
  | CallHandoffBody
  | MeetingCreateBody
  | MeetingUpdateBody
  | MeetingRsvpBody
  | MeetingCancelBody;

/**
 * Successfully decoded renderable event. `type` is the numeric tag
 * from §2.1; `body` is the typed body shape for that tag.
 */
export interface ParsedAppEvent {
  readonly kind: AppEventKind;
  readonly type: AppEventType;
  readonly body: AppEventBody;
  /**
   * Optional `parent_id` (reply target) — promoted from the envelope's
   * top-level `parent_id` per §1.1 key 4. The JSON wrapper carries
   * this in `payload.parent_id` until CBOR migration.
   */
  readonly parent_id?: string | undefined;
  /** Optional `thread_id` (§1.1 key 5). */
  readonly thread_id?: string | undefined;
}

/**
 * Sidecar envelopes (receipts, profile bootstrap). Surface them as a
 * distinct discriminant so callers branch deterministically — they
 * never produce a chat-room bubble or a chat-list preview update.
 */
export interface ParsedSidecar {
  readonly kind: SidecarKind;
  readonly payload: Record<string, unknown>;
}

/** Unknown / forward-compat fallback — §1.3 rule 3. */
export interface ParsedUnrenderable {
  readonly kind: 'unrenderable';
  /** Raw kind string off the wire, for debugging only. */
  readonly rawKind: string | undefined;
  /** Whole envelope as parsed JSON, for forward compatibility. */
  readonly raw: unknown;
}

export type ParseResult = ParsedAppEvent | ParsedSidecar | ParsedUnrenderable;

const TEXT_DECODER =
  typeof TextDecoder !== 'undefined' ? new TextDecoder() : undefined;

function bytesToString(bytes: Uint8Array): string {
  if (TEXT_DECODER) return TEXT_DECODER.decode(bytes);
  // Fallback for the (uncommon) Hermes-without-TextDecoder path.
  let s = '';
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i] ?? 0);
  return s;
}

/**
 * Parse a plaintext envelope. Accepts either `Uint8Array` (off the
 * SDK's `onMessage` callback) or `string` (off `Message.body` in
 * persistence). Returns `undefined` only when the input is itself
 * empty / nullish; every other input maps to a `ParseResult`.
 */
export function parseEnvelope(
  input: Uint8Array | string | undefined | null,
): ParseResult | undefined {
  if (input === undefined || input === null) return undefined;
  const text = typeof input === 'string' ? input : bytesToString(input);
  if (text.length === 0) return undefined;

  // Fast-path: a body that doesn't start with `{` is a legacy plain
  // text payload from a pre-wrapper client. Promote to a TextBody so
  // the rest of the pipeline doesn't need a special case.
  if (text[0] !== '{') {
    return {
      kind: 'com.ping.text',
      type: 0x10,
      body: { body: text },
    };
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    // Treat malformed JSON as legacy plain text too. Better to show
    // *something* than to drop the row.
    return {
      kind: 'com.ping.text',
      type: 0x10,
      body: { body: text },
    };
  }

  if (parsed === null || typeof parsed !== 'object') {
    return {
      kind: 'unrenderable',
      rawKind: undefined,
      raw: parsed,
    };
  }

  const env = parsed as Record<string, unknown>;
  const rawKind = typeof env.kind === 'string' ? env.kind : undefined;
  const payload =
    env.payload !== null && typeof env.payload === 'object'
      ? (env.payload as Record<string, unknown>)
      : {};

  if (isSidecarKind(rawKind)) {
    return { kind: rawKind, payload };
  }

  if (!isAppEventKind(rawKind)) {
    return {
      kind: 'unrenderable',
      rawKind,
      raw: parsed,
    };
  }

  const type = appEventTypeForKind(rawKind);
  if (type === undefined) {
    return { kind: 'unrenderable', rawKind, raw: parsed };
  }

  const body = decodePayload(rawKind, payload);
  const parent_id = readOptionalString(payload.parent_id ?? env.parent_id);
  const thread_id = readOptionalString(payload.thread_id ?? env.thread_id);

  const result: ParsedAppEvent = {
    kind: rawKind,
    type,
    body,
    ...(parent_id !== undefined ? { parent_id } : {}),
    ...(thread_id !== undefined ? { thread_id } : {}),
  };
  return result;
}

// -----------------------------------------------------------------
// Per-kind payload decoders. Each one normalizes the JSON-wrapper
// shape onto the typed body interface and tolerates missing keys
// (§1.3 forward-compat) by defaulting them.
// -----------------------------------------------------------------

function decodePayload(
  kind: AppEventKind,
  p: Record<string, unknown>,
): AppEventBody {
  switch (kind) {
    case 'com.ping.text':
      return decodeText(p);
    case 'com.ping.image':
      return decodeImage(p);
    case 'com.ping.video':
      return decodeVideo(p);
    case 'com.ping.voice':
      return decodeVoice(p);
    case 'com.ping.file':
      return decodeFile(p);
    case 'com.ping.location':
      return decodeLocation(p);
    case 'com.ping.sticker':
      return decodeSticker(p);
    case 'com.ping.gif':
      return decodeGif(p);
    case 'com.ping.link_preview':
      return decodeLinkPreview(p);
    case 'com.ping.edit':
      return decodeEdit(p);
    case 'com.ping.redact':
      return decodeRedact(p);
    case 'com.ping.forward':
      return decodeForward(p);
    case 'com.ping.reaction':
      return decodeReaction(p);
    case 'com.ping.reaction_remove':
      return decodeReactionRemove(p);
    case 'com.ping.thread_root':
      return decodeThreadRoot(p);
    case 'com.ping.group_name':
      return decodeGroupName(p);
    case 'com.ping.group_avatar':
      return decodeGroupAvatar(p);
    case 'com.ping.group_topic':
      return decodeGroupTopic(p);
    case 'com.ping.invite':
      return decodeInvite(p);
    case 'com.ping.kick':
      return decodeKick(p);
    case 'com.ping.pin':
      return decodePin(p);
    case 'com.ping.unpin':
      return decodeUnpin(p);
    case 'com.ping.save':
      return decodeSave(p);
    case 'com.ping.unsave':
      return decodeUnsave(p);
    // Starred is DeviceGroup-only personal state, applied directly in
    // `useIncomingMessages` (suppressed before this render-path decode is ever
    // reached). The `{target}` body here only keeps the switch exhaustive.
    case 'com.ping.star':
      return decodeSave(p);
    case 'com.ping.blocked_user_upsert':
      return decodeBlockedUserUpsert(p);
    case 'com.ping.blocked_user_remove':
      return decodeBlockedUserRemove(p);
    case 'com.ping.disappearing_policy':
      return decodeDisappearingPolicy(p);
    case 'com.ping.member_role':
      return decodeMemberRole(p);
    case 'com.ping.poll':
      return decodePoll(p);
    case 'com.ping.poll_vote':
      return decodePollVote(p);
    case 'com.ping.note_upsert':
      return decodeNoteUpsert(p);
    case 'com.ping.note_delete':
      return decodeNoteDelete(p);
    case 'com.ping.task_upsert':
      return decodeTaskUpsert(p);
    case 'com.ping.task_delete':
      return decodeTaskDelete(p);
    case 'com.ping.self_profile':
      return {};
    case 'com.ping.conv_state':
      return {
        conversationId: readString(p.conversation_id ?? p.conversationId),
        patch:
          p.patch !== null && typeof p.patch === 'object'
            ? (p.patch as Record<string, unknown>)
            : {},
      };
    case 'com.ping.read_pointer':
      return decodeReadPointer(p);
    case 'com.ping.reminder_set':
      return decodeReminderSet(p);
    case 'com.ping.reminder_clear':
      return decodeReminderClear(p);
    case 'com.ping.tour_seen':
      return decodeTourSeen(p);
    case 'com.ping.game_create':
      return decodeGameCreate(p);
    case 'com.ping.game_join':
      return decodeGameJoin(p);
    case 'com.ping.game_move':
      return decodeGameMove(p);
    case 'com.ping.game_reveal':
      return decodeGameReveal(p);
    case 'com.ping.game_outcome':
      return decodeGameOutcome(p);
    case 'com.ping.game_abort':
      return decodeGameAbort(p);
    case 'com.ping.call_invite':
      return decodeCallInvite(p);
    case 'com.ping.call_accept':
      return decodeCallAccept(p);
    case 'com.ping.call_decline':
      return decodeCallDecline(p);
    case 'com.ping.call_end':
      return decodeCallEnd(p);
    case 'com.ping.call_ringing':
      return decodeCallRinging(p);
    case 'com.ping.call_hand_raise':
      return decodeCallHandRaise(p);
    case 'com.ping.call_reaction':
      return decodeCallReaction(p);
    case 'com.ping.call_roster':
      return decodeCallRoster(p);
    case 'com.ping.call_handoff':
      return decodeCallHandoff(p);
    case 'com.ping.meeting':
      return decodeMeeting(p);
    case 'com.ping.meeting_update':
      return decodeMeetingUpdate(p);
    case 'com.ping.meeting_rsvp':
      return decodeMeetingRsvp(p);
    case 'com.ping.meeting_cancel':
      return decodeMeetingCancel(p);
  }
}

function readString(v: unknown, fallback = ''): string {
  return typeof v === 'string' ? v : fallback;
}

function readOptionalString(v: unknown): string | undefined {
  return typeof v === 'string' && v.length > 0 ? v : undefined;
}

function readNumber(v: unknown, fallback = 0): number {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'bigint') return Number(v);
  return fallback;
}

function readOptionalNumber(v: unknown): number | undefined {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'bigint') return Number(v);
  return undefined;
}

function readBool(v: unknown, fallback = false): boolean {
  return typeof v === 'boolean' ? v : fallback;
}

function readStringArray(v: unknown): ReadonlyArray<string> {
  if (!Array.isArray(v)) return [];
  return v.filter((x): x is string => typeof x === 'string');
}

function readNumberArray(v: unknown): ReadonlyArray<number> {
  if (!Array.isArray(v)) return [];
  return v.filter((x): x is number => typeof x === 'number');
}

function readBlobRef(v: unknown): BlobRef {
  if (v === null || typeof v !== 'object') return {};
  const r = v as Record<string, unknown>;
  const ref: BlobRef = {};
  const mediaId = readOptionalString(r.media_id);
  const contentKey = readOptionalString(r.content_key);
  const contentIv = readOptionalString(r.content_iv);
  const hash = readOptionalString(r.hash);
  const url = readOptionalString(r.url);
  const bytes = readOptionalString(r.bytes);
  return {
    ...(mediaId !== undefined ? { media_id: mediaId } : {}),
    ...(contentKey !== undefined ? { content_key: contentKey } : {}),
    ...(contentIv !== undefined ? { content_iv: contentIv } : {}),
    ...(hash !== undefined ? { hash } : {}),
    ...(url !== undefined ? { url } : {}),
    ...(bytes !== undefined ? { bytes } : {}),
    ...ref,
  };
}

function readMentions(v: unknown): ReadonlyArray<Mention> | undefined {
  if (!Array.isArray(v) || v.length === 0) return undefined;
  const out: Mention[] = [];
  for (const item of v) {
    if (!item || typeof item !== 'object') continue;
    const m = item as Record<string, unknown>;
    const userId = readOptionalString(m.userId ?? m.user_id);
    const range = m.range && typeof m.range === 'object'
      ? (m.range as Record<string, unknown>)
      : undefined;
    if (!userId || !range) continue;
    const start = readOptionalNumber(range.start);
    const end = readOptionalNumber(range.end);
    if (start === undefined || end === undefined) continue;
    const displayName = readOptionalString(m.displayName ?? m.display_name);
    out.push({
      userId,
      range: { start, end },
      ...(displayName !== undefined ? { displayName } : {}),
    });
  }
  return out.length > 0 ? out : undefined;
}

function decodeText(p: Record<string, unknown>): TextBody {
  const format = p.format === 'rich' ? 'rich' : 'plain';
  const mentions = readMentions(p.mentions);
  const spoilers = Array.isArray(p.spoilers)
    ? p.spoilers
        .map((s) => (s && typeof s === 'object' ? (s as Record<string, unknown>) : null))
        .filter((s): s is Record<string, unknown> => s !== null)
        .map((s) => ({
          start: readNumber(s.start),
          end: readNumber(s.end),
        }))
    : undefined;
  const code_blocks = Array.isArray(p.code_blocks)
    ? p.code_blocks
        .map((s) => (s && typeof s === 'object' ? (s as Record<string, unknown>) : null))
        .filter((s): s is Record<string, unknown> => s !== null)
        .map((s) => {
          const range =
            s.range && typeof s.range === 'object'
              ? (s.range as Record<string, unknown>)
              : { start: 0, end: 0 };
          const language = readOptionalString(s.language);
          return {
            range: { start: readNumber(range.start), end: readNumber(range.end) },
            ...(language !== undefined ? { language } : {}),
          };
        })
    : undefined;
  return {
    body: readString(p.body),
    format,
    ...(mentions !== undefined ? { mentions } : {}),
    ...(spoilers !== undefined && spoilers.length > 0 ? { spoilers } : {}),
    ...(code_blocks !== undefined && code_blocks.length > 0
      ? { code_blocks }
      : {}),
  };
}

function decodeImage(p: Record<string, unknown>): ImageBody {
  const blurhash = readOptionalString(p.blurhash);
  const caption = readOptionalString(p.caption);
  const thumbRaw = p.thumb_ref;
  const thumb_ref =
    thumbRaw !== undefined ? readBlobRef(thumbRaw) : undefined;
  return {
    blob_ref: readBlobRef(p.blob_ref ?? p),
    w: readNumber(p.w),
    h: readNumber(p.h),
    mime: readString(p.mime, 'image/*'),
    ...(blurhash !== undefined ? { blurhash } : {}),
    ...(caption !== undefined ? { caption } : {}),
    ...(thumb_ref !== undefined ? { thumb_ref } : {}),
  };
}

function decodeVideo(p: Record<string, unknown>): VideoBody {
  const caption = readOptionalString(p.caption);
  const poster_ref =
    p.poster_ref !== undefined ? readBlobRef(p.poster_ref) : undefined;
  return {
    blob_ref: readBlobRef(p.blob_ref ?? p),
    w: readNumber(p.w),
    h: readNumber(p.h),
    dur_ms: readNumber(p.dur_ms ?? p.durationMs),
    mime: readString(p.mime, 'video/*'),
    ...(caption !== undefined ? { caption } : {}),
    ...(poster_ref !== undefined ? { poster_ref } : {}),
  };
}

function decodeVoice(p: Record<string, unknown>): VoiceBody {
  // Legacy `sendVoiceMessage` envelope puts mime + base64 bytes at
  // the payload root; promote them into `blob_ref` so the tile
  // dispatch sees a uniform shape.
  const inlineBytes = readOptionalString(p.bytes);
  const inlineMime = readOptionalString(p.mime);
  const blobRefRaw = p.blob_ref;
  const blob_ref =
    blobRefRaw !== undefined
      ? readBlobRef(blobRefRaw)
      : ({
          ...(inlineBytes !== undefined ? { bytes: inlineBytes } : {}),
          ...(inlineMime !== undefined ? { content_iv: undefined } : {}),
        } as BlobRef);
  return {
    blob_ref,
    dur_ms: readNumber(p.dur_ms ?? p.durationMs),
    waveform: readNumberArray(p.waveform),
  };
}

function decodeFile(p: Record<string, unknown>): FileBody {
  const page_count = readOptionalNumber(p.page_count ?? p.pageCount);
  return {
    blob_ref: readBlobRef(p.blob_ref ?? p),
    name: readString(p.name ?? p.filename),
    size: readNumber(p.size ?? p.byteSize),
    mime: readString(p.mime ?? p.mimeType, 'application/octet-stream'),
    ...(page_count !== undefined ? { page_count } : {}),
  };
}

function decodeLocation(p: Record<string, unknown>): LocationOnceBody {
  const label = readOptionalString(p.label);
  return {
    lat: readNumber(p.lat),
    lon: readNumber(p.lon),
    accuracy_m: readNumber(p.accuracy_m),
    ...(label !== undefined ? { label } : {}),
  };
}

/**
 * GIF envelope decoder. The web client wraps Tenor/KLIPY picks as
 *   { kind: 'com.ping.gif',
 *     payload: { url, width, height, altText? } }
 * which we normalize onto the doc-spec `ImageBody` shape so the
 * downstream tile + preview logic doesn't need a special case.
 * The mime is hard-coded to `image/gif` since the wire kind already
 * disambiguates.
 */
function decodeGif(p: Record<string, unknown>): ImageBody {
  const url = readOptionalString(p.url);
  const caption = readOptionalString(p.altText ?? p.caption);
  return {
    blob_ref: url !== undefined ? { url } : {},
    w: readNumber(p.width ?? p.w),
    h: readNumber(p.height ?? p.h),
    mime: 'image/gif',
    ...(caption !== undefined ? { caption } : {}),
    // A GIF envelope tagged `sticker: true` is a sticker (renders chrome-free).
    ...(p.sticker === true ? { sticker: true } : {}),
  };
}

function decodeSticker(p: Record<string, unknown>): StickerBody {
  const fallback_blob_ref =
    p.fallback_blob_ref !== undefined
      ? readBlobRef(p.fallback_blob_ref)
      : undefined;
  return {
    pack_id: readString(p.pack_id ?? p.packId),
    sticker_id: readString(p.sticker_id ?? p.stickerId),
    ...(fallback_blob_ref !== undefined ? { fallback_blob_ref } : {}),
  };
}

function decodeLinkPreview(p: Record<string, unknown>): LinkPreviewBody {
  const desc = readOptionalString(p.desc);
  const image_ref =
    p.image_ref !== undefined ? readBlobRef(p.image_ref) : undefined;
  return {
    url: readString(p.url),
    title: readString(p.title),
    ...(desc !== undefined ? { desc } : {}),
    ...(image_ref !== undefined ? { image_ref } : {}),
  };
}

function decodeEdit(p: Record<string, unknown>): EditBody {
  const new_text = readOptionalString(p.new_text ?? p.body);
  return {
    target: readString(p.target),
    new_type: readNumber(p.new_type, 0x10),
    ...(new_text !== undefined ? { new_text } : {}),
    ...(p.new_body !== undefined ? { new_body: p.new_body } : {}),
  };
}

function decodeRedact(p: Record<string, unknown>): RedactBody {
  const reason = readOptionalString(p.reason);
  const validReason =
    reason === 'user' || reason === 'moderation' || reason === 'retention'
      ? reason
      : undefined;
  return {
    target: readString(p.target),
    ...(validReason !== undefined ? { reason: validReason } : {}),
  };
}

function decodeForward(p: Record<string, unknown>): ForwardBody {
  const original_sender = readOptionalString(p.original_sender);
  return {
    source_event_id: readString(p.source_event_id),
    source_conversation_id: readString(p.source_conversation_id),
    content_type: readNumber(p.content_type, 0x10),
    ...(original_sender !== undefined ? { original_sender } : {}),
    ...(p.content_body !== undefined ? { content_body: p.content_body } : {}),
  };
}

function decodeReaction(p: Record<string, unknown>): ReactionBody {
  return {
    target: readString(p.target),
    emoji: readString(p.emoji),
  };
}

function decodeReactionRemove(p: Record<string, unknown>): ReactionRemoveBody {
  return {
    target: readString(p.target),
    emoji: readString(p.emoji),
  };
}

function decodeThreadRoot(p: Record<string, unknown>): ThreadRootBody {
  return {
    from: readString(p.from),
  };
}

function decodeGroupName(p: Record<string, unknown>): GroupNameBody {
  const actor = readOptionalString(p.actorDisplayName);
  const avatarMediaId = readOptionalString(p.avatarMediaId);
  const memberAccountIds = Array.isArray(p.memberAccountIds)
    ? p.memberAccountIds.filter((id): id is string => typeof id === 'string' && id.length > 0)
    : undefined;
  let memberDisplayNames: Record<string, string> | undefined;
  if (typeof p.memberDisplayNames === 'object' && p.memberDisplayNames !== null) {
    const out: Record<string, string> = {};
    for (const [id, label] of Object.entries(p.memberDisplayNames as Record<string, unknown>)) {
      if (typeof label === 'string' && label.length > 0) out[id] = label;
    }
    if (Object.keys(out).length > 0) memberDisplayNames = out;
  }
  return {
    name: readString(p.name),
    ...(actor !== undefined ? { actorDisplayName: actor } : {}),
    ...(avatarMediaId !== undefined ? { avatarMediaId } : {}),
    ...(memberAccountIds !== undefined && memberAccountIds.length > 0
      ? { memberAccountIds }
      : {}),
    ...(memberDisplayNames !== undefined ? { memberDisplayNames } : {}),
  };
}

function decodeGroupAvatar(p: Record<string, unknown>): GroupAvatarBody {
  const actor = readOptionalString(p.actorDisplayName);
  return {
    blob_ref: readBlobRef(p.blob_ref ?? p),
    ...(actor !== undefined ? { actorDisplayName: actor } : {}),
  };
}

function decodeGroupTopic(p: Record<string, unknown>): GroupTopicBody {
  const actor = readOptionalString(p.actorDisplayName);
  return {
    topic: readString(p.topic),
    ...(actor !== undefined ? { actorDisplayName: actor } : {}),
  };
}

function decodeInvite(p: Record<string, unknown>): InviteBody {
  const kindRaw = readOptionalString(p.invite_kind);
  const invite_kind =
    kindRaw === 'link' || kindRaw === 'alias' ? kindRaw : 'user';
  const actor = readOptionalString(p.actorDisplayName);
  const invitee = readOptionalString(p.inviteeDisplayName);
  const expires_at = readOptionalNumber(p.expires_at);
  const inviteeAccountId = readOptionalString(p.inviteeAccountId);
  const memberAccountIds = Array.isArray(p.memberAccountIds)
    ? p.memberAccountIds.filter((id): id is string => typeof id === 'string' && id.length > 0)
    : undefined;
  let memberDisplayNames: Record<string, string> | undefined;
  if (typeof p.memberDisplayNames === 'object' && p.memberDisplayNames !== null) {
    const out: Record<string, string> = {};
    for (const [id, label] of Object.entries(p.memberDisplayNames as Record<string, unknown>)) {
      if (typeof label === 'string' && label.length > 0) out[id] = label;
    }
    if (Object.keys(out).length > 0) memberDisplayNames = out;
  }
  return {
    invitee: readString(p.invitee),
    invite_kind,
    ...(expires_at !== undefined ? { expires_at } : {}),
    ...(actor !== undefined ? { actorDisplayName: actor } : {}),
    ...(invitee !== undefined ? { inviteeDisplayName: invitee } : {}),
    ...(inviteeAccountId !== undefined ? { inviteeAccountId } : {}),
    ...(memberAccountIds !== undefined && memberAccountIds.length > 0
      ? { memberAccountIds }
      : {}),
    ...(memberDisplayNames !== undefined ? { memberDisplayNames } : {}),
  };
}

function decodeKick(p: Record<string, unknown>): KickReasonBody {
  const actor = readOptionalString(p.actorDisplayName);
  const target = readOptionalString(p.targetDisplayName);
  return {
    target_user_id: readString(p.target_user_id),
    reason: readString(p.reason),
    ...(actor !== undefined ? { actorDisplayName: actor } : {}),
    ...(target !== undefined ? { targetDisplayName: target } : {}),
  };
}

function decodePin(p: Record<string, unknown>): PinBody {
  const actor = readOptionalString(p.actorDisplayName);
  return {
    target: readString(p.target),
    ...(actor !== undefined ? { actorDisplayName: actor } : {}),
  };
}

function decodeUnpin(p: Record<string, unknown>): UnpinBody {
  const actor = readOptionalString(p.actorDisplayName);
  return {
    target: readString(p.target),
    ...(actor !== undefined ? { actorDisplayName: actor } : {}),
  };
}

function decodeSave(p: Record<string, unknown>): SaveBody {
  return { target: readString(p.target) };
}

function decodeUnsave(p: Record<string, unknown>): UnsaveBody {
  return { target: readString(p.target) };
}

function decodeBlockedUserUpsert(p: Record<string, unknown>): BlockedUserUpsertBody {
  return {
    blocked_user_id: readString(p.blocked_user_id),
    blocked_at: readNumber(p.blocked_at),
  };
}

function decodeBlockedUserRemove(p: Record<string, unknown>): BlockedUserRemoveBody {
  return { blocked_user_id: readString(p.blocked_user_id) };
}

function decodeDisappearingPolicy(p: Record<string, unknown>): DisappearingPolicyBody {
  const scope = readString(p.scope) === 'event' ? 'event' : 'conversation';
  const target = readOptionalString(p.target_event_id);
  return {
    scope,
    ttl_seconds: readNumber(p.ttl_seconds),
    ...(target !== undefined ? { target_event_id: target } : {}),
  };
}

function decodeMemberRole(p: Record<string, unknown>): MemberRoleSetBody {
  const raw = readString(p.role);
  const role: GroupMemberRole = raw === 'owner' || raw === 'admin' ? raw : 'member';
  const actor = readOptionalString(p.actorDisplayName);
  return {
    target_user_id: readString(p.target_user_id),
    role,
    ...(actor !== undefined ? { actorDisplayName: actor } : {}),
  };
}

function decodePoll(p: Record<string, unknown>): PollCreateBody {
  const closes_at = readOptionalNumber(p.closes_at);
  return {
    question: readString(p.question),
    options: readStringArray(p.options),
    multi_select: readBool(p.multi_select),
    anonymous: readBool(p.anonymous),
    ...(closes_at !== undefined ? { closes_at } : {}),
  };
}

function decodePollVote(p: Record<string, unknown>): PollVoteBody {
  // Accept the JSON-wrapper `target`, plus the doc's `poll` and a
  // convenience `poll_id` alias, all carrying the poll_create content
  // hash. `option_index` is the single-choice selection; a multi-select
  // sender additionally ships the doc's `choices: number[]` — the actor's
  // FULL selected set — which the vote-reducer uses to REPLACE the actor's
  // prior rows. `option_index` stays populated (= choices[0]) so
  // single-choice consumers keep working unchanged.
  const target = readString(p.target ?? p.poll ?? p.poll_id);
  const choices = Array.isArray(p.choices)
    ? (p.choices as unknown[])
        .map((c) => readOptionalNumber(c))
        .filter((c): c is number => c !== undefined)
    : [];
  const option_index = readNumber(p.option_index ?? choices[0]);
  const actorAccountId = readOptionalString(p.actorAccountId ?? p.actor_account_id);
  return {
    target,
    option_index,
    ...(choices.length > 0 ? { choices } : {}),
    ...(actorAccountId !== undefined ? { actorAccountId } : {}),
  };
}

function decodeNoteUpsert(p: Record<string, unknown>): NoteUpsertBody {
  const tags = readStringArray(p.tags);
  return {
    note_id: readString(p.note_id ?? p.noteId),
    title: readString(p.title),
    body: readString(p.body),
    updated_at: readNumber(p.updated_at ?? p.updatedAt),
    ...(tags.length > 0 ? { tags } : {}),
  };
}

function decodeNoteDelete(p: Record<string, unknown>): NoteDeleteBody {
  return {
    note_id: readString(p.note_id ?? p.noteId),
  };
}

function decodeTaskUpsert(p: Record<string, unknown>): TaskUpsertBody {
  const due = readOptionalNumber(p.due);
  const done = typeof p.done === 'boolean' ? p.done : undefined;
  const list = readOptionalString(p.list);
  const priorityRaw = p.priority;
  const priority =
    priorityRaw === 'low' ||
    priorityRaw === 'medium' ||
    priorityRaw === 'high' ||
    priorityRaw === 'urgent'
      ? priorityRaw
      : undefined;
  const stateRaw = p.state;
  const state =
    stateRaw === 'backlog' ||
    stateRaw === 'pending' ||
    stateRaw === 'inProgress' ||
    stateRaw === 'completed' ||
    stateRaw === 'cancelled'
      ? stateRaw
      : undefined;
  const assigneeId = readOptionalString(p.assignee_id ?? p.assigneeId);
  const description = readOptionalString(p.description);
  const updatedAt = readOptionalNumber(p.updated_at ?? p.updatedAt);
  return {
    task_id: readString(p.task_id ?? p.taskId),
    title: readString(p.title),
    position: readString(p.position),
    ...(due !== undefined ? { due } : {}),
    ...(done !== undefined ? { done } : {}),
    ...(list !== undefined ? { list } : {}),
    ...(priority !== undefined ? { priority } : {}),
    ...(state !== undefined ? { state } : {}),
    ...(assigneeId !== undefined ? { assignee_id: assigneeId } : {}),
    ...(description !== undefined ? { description } : {}),
    ...(updatedAt !== undefined ? { updated_at: updatedAt } : {}),
  };
}

function decodeReadPointer(p: Record<string, unknown>): ReadPointerBody {
  const lastReadMessageId = readOptionalString(
    p.last_read_message_id ?? p.lastReadMessageId,
  );
  return {
    conversation_id: readString(p.conversation_id ?? p.conversationId),
    last_read_at: readNumber(p.last_read_at ?? p.lastReadAt),
    ...(lastReadMessageId !== undefined
      ? { last_read_message_id: lastReadMessageId }
      : {}),
  };
}

function decodeReminderSet(p: Record<string, unknown>): ReminderSetBody {
  const messageId = readOptionalString(p.message_id ?? p.messageId);
  const note = readOptionalString(p.note);
  return {
    reminder_id: readString(p.reminder_id ?? p.reminderId),
    conversation_id: readString(p.conversation_id ?? p.conversationId),
    fire_at: readNumber(p.fire_at ?? p.fireAt),
    created_at: readNumber(p.created_at ?? p.createdAt),
    ...(messageId !== undefined ? { message_id: messageId } : {}),
    ...(note !== undefined ? { note } : {}),
  };
}

function decodeReminderClear(p: Record<string, unknown>): ReminderClearBody {
  return {
    reminder_id: readString(p.reminder_id ?? p.reminderId),
  };
}

function decodeTourSeen(p: Record<string, unknown>): TourSeenBody {
  return {
    tour_id: readString(p.tour_id ?? p.tourId),
    seen_at: readNumber(p.seen_at ?? p.seenAt),
  };
}

function decodeTaskDelete(p: Record<string, unknown>): TaskDeleteBody {
  return {
    task_id: readString(p.task_id ?? p.taskId),
  };
}

const GAME_KINDS: ReadonlyArray<GameKind> = [
  'coinflip',
  'dice',
  'rps',
  'chess',
  'blackjack',
  'racing',
];

function readGameKind(v: unknown): GameKind {
  return typeof v === 'string' && (GAME_KINDS as ReadonlyArray<string>).includes(v)
    ? (v as GameKind)
    : 'coinflip';
}

function readGameParams(v: unknown): Record<string, unknown> {
  return v !== null && typeof v === 'object'
    ? (v as Record<string, unknown>)
    : {};
}

function readGameStakes(v: unknown): GameStakes | undefined {
  if (v === null || typeof v !== 'object') return undefined;
  const kind = (v as Record<string, unknown>).kind;
  if (kind === 'leaderboard' || kind === 'none') return { kind };
  return undefined;
}

function readGamePayouts(v: unknown): ReadonlyArray<GamePayout> | undefined {
  if (!Array.isArray(v) || v.length === 0) return undefined;
  const out: GamePayout[] = [];
  for (const item of v) {
    if (!item || typeof item !== 'object') continue;
    const r = item as Record<string, unknown>;
    const userId = readOptionalString(r.user_id ?? r.userId);
    if (userId === undefined) continue;
    out.push({ user_id: userId, points: readNumber(r.points) });
  }
  return out.length > 0 ? out : undefined;
}

function decodeGameCreate(p: Record<string, unknown>): GameCreateBody {
  const stakes = readGameStakes(p.stakes);
  return {
    game_kind: readGameKind(p.game_kind ?? p.gameKind),
    session_id: readString(p.session_id ?? p.sessionId),
    params: readGameParams(p.params),
    ...(stakes !== undefined ? { stakes } : {}),
  };
}

function decodeGameJoin(p: Record<string, unknown>): GameJoinBody {
  return {
    session_id: readString(p.session_id ?? p.sessionId),
    seat: readNumber(p.seat),
  };
}

function decodeGameMove(p: Record<string, unknown>): GameMoveBody {
  const commit = readOptionalString(p.commit);
  return {
    session_id: readString(p.session_id ?? p.sessionId),
    move_idx: readNumber(p.move_idx ?? p.moveIdx),
    payload: readString(p.payload),
    ...(commit !== undefined ? { commit } : {}),
  };
}

function decodeGameReveal(p: Record<string, unknown>): GameRevealBody {
  return {
    session_id: readString(p.session_id ?? p.sessionId),
    move_idx: readNumber(p.move_idx ?? p.moveIdx),
    salt: readString(p.salt),
    value: readString(p.value),
  };
}

function decodeGameOutcome(p: Record<string, unknown>): GameOutcomeBody {
  const winner = readOptionalString(p.winner);
  const payouts = readGamePayouts(p.payouts);
  return {
    session_id: readString(p.session_id ?? p.sessionId),
    final_state: readString(p.final_state ?? p.finalState),
    ...(winner !== undefined ? { winner } : {}),
    ...(payouts !== undefined ? { payouts } : {}),
  };
}

function decodeGameAbort(p: Record<string, unknown>): GameAbortBody {
  const reasonRaw = readOptionalString(p.reason);
  const reason =
    reasonRaw === 'leave' || reasonRaw === 'invalid_state'
      ? reasonRaw
      : 'timeout';
  return {
    session_id: readString(p.session_id ?? p.sessionId),
    reason,
  };
}

function decodeCallInvite(p: Record<string, unknown>): CallInviteBody {
  const media = p.media === 'video' ? 'video' : 'audio';
  const actor = readOptionalString(p.actorDisplayName);
  return {
    call_id: readString(p.call_id),
    call_group_id: readString(p.call_group_id),
    media,
    initiator_device: readString(p.initiator_device),
    ...(actor !== undefined ? { actorDisplayName: actor } : {}),
    ...(typeof p.group === 'boolean' ? { group: p.group } : {}),
  };
}

function decodeCallEnd(p: Record<string, unknown>): CallEndBody {
  const duration_ms = readOptionalNumber(p.duration_ms);
  // Preserve the participant roster — it drives the ended-tile avatar stack +
  // per-viewer "missed" for EVERY member. Was previously dropped here, so even
  // though `endCall` emits `participants`, the decoded body never carried it →
  // the ended tile always rendered with no participants.
  const participants = readStringArray(p.participants);
  return {
    call_id: readString(p.call_id),
    reason: readString(p.reason, 'ended'),
    ...(duration_ms !== undefined ? { duration_ms } : {}),
    ...(participants.length > 0 ? { participants } : {}),
  };
}

// Ephemeral live-roster presence ping (see CallType.CALL_ROSTER). Carries the
// CURRENT set of in-call participant account ids so members NOT in the call
// render a live count/avatars on the ongoing tile.
function decodeCallRoster(p: Record<string, unknown>): CallRosterBody {
  return {
    call_id: readString(p.call_id),
    participants: readStringArray(p.participants),
  };
}

function decodeCallAccept(p: Record<string, unknown>): CallAcceptBody {
  return {
    call_id: readString(p.call_id),
    call_group_id: readString(p.call_group_id),
    accepting_device: readString(p.accepting_device),
  };
}

function decodeCallDecline(p: Record<string, unknown>): CallDeclineBody {
  const reason =
    p.reason === 'timeout' || p.reason === 'busy' ? p.reason : 'declined';
  return {
    call_id: readString(p.call_id),
    reason,
  };
}

function decodeCallRinging(p: Record<string, unknown>): CallRingingBody {
  return { call_id: readString(p.call_id) };
}

function decodeCallHandRaise(p: Record<string, unknown>): CallHandRaiseBody {
  return {
    call_id: readString(p.call_id),
    raised: p.raised === true,
  };
}

function decodeCallReaction(p: Record<string, unknown>): CallReactionBody {
  return {
    call_id: readString(p.call_id),
    emoji: readString(p.emoji),
  };
}

function decodeCallHandoff(p: Record<string, unknown>): CallHandoffBody {
  return {
    call_id: readString(p.call_id),
    call_group_id: readString(p.call_group_id),
    media: p.media === 'video' ? 'video' : 'audio',
    account: readString(p.account),
    from_device: readString(p.from_device),
  };
}

function decodeMeetingAutoCall(v: unknown): MeetingCreateBody['auto_call'] {
  if (typeof v !== 'object' || v === null) return undefined;
  const r = v as Record<string, unknown>;
  const media = r.media === 'video' ? 'video' : 'audio';
  return { media, pre_invite_minutes: readNumber(r.pre_invite_minutes) };
}

function decodeMeeting(p: Record<string, unknown>): MeetingCreateBody {
  const location = readOptionalString(p.location);
  const description = readOptionalString(p.description);
  const meetingLink = readOptionalString(p.meeting_link ?? p.meetingLink);
  const autoCall = decodeMeetingAutoCall(p.auto_call);
  return {
    meeting_id: readString(p.meeting_id),
    title: readString(p.title),
    starts_at: readNumber(p.starts_at),
    ends_at: readNumber(p.ends_at),
    attendee_user_ids: readStringArray(p.attendee_user_ids),
    ...(location !== undefined ? { location } : {}),
    ...(description !== undefined ? { description } : {}),
    ...(meetingLink !== undefined ? { meeting_link: meetingLink } : {}),
    ...(autoCall !== undefined ? { auto_call: autoCall } : {}),
  };
}

/** MEETING_UPDATE is a partial diff — an ABSENT key is unchanged, an empty
 *  STRING clears the field. So string fields must preserve `""` (unlike
 *  `readOptionalString`, which collapses it to `undefined`). */
function updateString(v: unknown): string | undefined {
  return typeof v === 'string' ? v : undefined;
}

function decodeMeetingUpdate(p: Record<string, unknown>): MeetingUpdateBody {
  const title = readOptionalString(p.title); // title never "clears"
  const starts_at = readOptionalNumber(p.starts_at);
  const ends_at = readOptionalNumber(p.ends_at);
  const location = updateString(p.location);
  const description = updateString(p.description);
  const meeting_link = updateString(p.meeting_link ?? p.meetingLink);
  const added = p.attendee_user_ids_added;
  const removed = p.attendee_user_ids_removed;
  return {
    meeting_id: readString(p.meeting_id),
    ...(title !== undefined ? { title } : {}),
    ...(starts_at !== undefined ? { starts_at } : {}),
    ...(ends_at !== undefined ? { ends_at } : {}),
    ...(location !== undefined ? { location } : {}),
    ...(description !== undefined ? { description } : {}),
    ...(meeting_link !== undefined ? { meeting_link } : {}),
    ...(Array.isArray(added) ? { attendee_user_ids_added: readStringArray(added) } : {}),
    ...(Array.isArray(removed) ? { attendee_user_ids_removed: readStringArray(removed) } : {}),
  };
}

function decodeMeetingRsvp(p: Record<string, unknown>): MeetingRsvpBody {
  const raw = p.response;
  // Coerce an unknown response to 'maybe' (unreachable for a conformant client;
  // the reducer additionally gates on meeting existence + not-canceled).
  const response = raw === 'yes' || raw === 'no' || raw === 'maybe' ? raw : 'maybe';
  const note = readOptionalString(p.note);
  return {
    meeting_id: readString(p.meeting_id),
    response,
    ...(note !== undefined ? { note } : {}),
  };
}

function decodeMeetingCancel(p: Record<string, unknown>): MeetingCancelBody {
  const reason = readOptionalString(p.reason);
  return {
    meeting_id: readString(p.meeting_id),
    ...(reason !== undefined ? { reason } : {}),
  };
}

// -----------------------------------------------------------------
// Encoder helpers — symmetric with the decoders. Used by senders to
// build the JSON wrapper. CBOR migration will add a sibling
// `encodeEnvelopeCbor` that returns a `Uint8Array`.
// -----------------------------------------------------------------

export interface EncodableEnvelope {
  readonly kind: AppEventKind;
  readonly payload: Record<string, unknown>;
  /**
   * Per-send unique id (`types-and-schemas.md` §1.1 `event_id`).
   * When omitted, [[mintEventId]] fills one in — required so identical
   * plaintext bodies ("hello" twice) produce distinct `content_hash`
   * values under CR-6 (hash over plaintext). Pass an explicit id on
   * outbox/UI retries so the wire bytes stay stable for server
   * send-idempotency.
   */
  readonly event_id?: string | undefined;
  /** Optional reply target (envelope key 4 in CBOR). */
  readonly parent_id?: string | undefined;
  /** Optional thread root (envelope key 5 in CBOR). */
  readonly thread_id?: string | undefined;
}

/**
 * Mint a fresh per-send `event_id`. Prefer this (then pass it through
 * [[encodeEnvelopeJsonBytes]] AND the local Dexie/outbox row id) so
 * local identity and wire identity stay aligned across retries.
 */
export function mintEventId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `evt-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function encodeEnvelopeJsonObject(env: EncodableEnvelope): Record<string, unknown> {
  const out: Record<string, unknown> = {
    kind: env.kind,
    // Always stamp event_id — CR-6 content_hash is SHA-256(plaintext), so
    // two sends of identical body must differ somewhere in the bytes or
    // message-service treats the second as a retry and skips fanout.
    event_id: env.event_id ?? mintEventId(),
    payload: env.payload,
  };
  if (env.parent_id !== undefined) out.parent_id = env.parent_id;
  if (env.thread_id !== undefined) out.thread_id = env.thread_id;
  return out;
}

/**
 * Serializes an envelope to a UTF-8 byte string ready for `conv.send`.
 * Drops nullish parent_id / thread_id so we don't ship literal
 * `"parent_id": null` over the wire. Always includes `event_id`.
 */
export function encodeEnvelopeJsonBytes(env: EncodableEnvelope): Uint8Array {
  return new TextEncoder().encode(JSON.stringify(encodeEnvelopeJsonObject(env)));
}

/** String form of `encodeEnvelopeJsonBytes` — handy for persistence. */
export function encodeEnvelopeJsonString(env: EncodableEnvelope): string {
  return JSON.stringify(encodeEnvelopeJsonObject(env));
}
